import type { TFunction } from 'i18next';
import {
  currentWelcomeWeather,
  weatherIcon,
  type WeatherCondition,
  type WeatherIconKind,
  type WeatherReading,
} from './welcome-weather.js';

/**
 * What the HUD weather widget shows: the condition icon and the temperature, from the same room
 * state reading as the welcome splash (`state.weather`, ADR-0024).
 */
export interface HudWeather {
  readonly icon: WeatherIconKind;
  /** Undefined while no fresh reading is available (the icon then shows a clear sky). */
  readonly condition?: Exclude<WeatherCondition, 'unknown'>;
  /** Whole degrees Celsius; undefined while no reading is available. */
  readonly temperatureC?: number;
}

/**
 * The weather in the decoded room state for a region of the map (`state.regionWeather`, one per
 * office, ADR-0037), falling back to the office weather (`state.weather`).
 */
export function weatherFor<T>(state: unknown, region?: string): T | undefined {
  const view = state as
    { weather?: T; regionWeather?: { get?: (key: string) => T | undefined } } | undefined;
  const own = region === undefined ? undefined : view?.regionWeather?.get?.(region);
  // No reading for the region yet (e.g. just after a restart): the office weather is better.
  const known = own && (own as { condition?: string }).condition !== 'unknown';
  return known ? own : view?.weather;
}

/** The weather the player sees: of their region if it has its own, else of the office. */
export function readWeatherState(state: unknown, region?: string): WeatherReading | undefined {
  const weather = weatherFor<WeatherReading>(state, region);
  return weather ? { condition: weather.condition, temperatureC: weather.temperatureC } : undefined;
}

export function hudWeather(now: Date, reading?: WeatherReading): HudWeather {
  const weather = currentWelcomeWeather(now, {}, reading);
  const icon = weatherIcon(weather);
  if (weather.condition === 'unknown') return { icon };
  return weather.temperatureC === undefined
    ? { icon, condition: weather.condition }
    : // `|| 0` turns a rounded -0 into a plain 0.
      { icon, condition: weather.condition, temperatureC: Math.round(weather.temperatureC) || 0 };
}

/** "-7 °C"-style short text shown next to the icon, or undefined without a reading. */
export function hudTemperatureText(weather: HudWeather, t: TFunction): string | undefined {
  return weather.temperatureC === undefined
    ? undefined
    : t('welcome.temperature', { value: weather.temperatureC });
}

/** Screen reader text: "Weather in Umeå: Snow, -7 °C" / "Vädret i Umeå: Snö, -7 °C". */
export function hudWeatherLabel(weather: HudWeather, t: TFunction, region?: string): string {
  const place = region ?? t('welcome.place');
  if (!weather.condition) return t('hud.weatherUnknown', { place });
  const details = [t(`welcome.condition.${weather.condition}`), hudTemperatureText(weather, t)]
    .filter(Boolean)
    .join(', ');
  return t('hud.weather', { place, details });
}
